import { memo, useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import { attestationApi, type AttestationImageKind, type AttestationTemplate } from '../../lib/reportsApi.js'
import { ModalShell } from '../common/ModalShell.js'
import { ImageUp, Trash2 } from 'lucide-react'

interface AttestationTemplateDialogProps {
  channelId: string
  onClose:   () => void
}

const EMPTY_TEMPLATE: AttestationTemplate = {
  directorName: '', directorTitle: '', city: '', introText: '', hasLogo: false, hasSignature: false,
}

/**
 * Éditeur du modèle d'attestation propre à la chaîne : textes (nom du
 * directeur/directrice, fonction, ville, texte d'introduction) et images
 * (logo d'en-tête, signature). Les images sont stockées côté serveur ; on
 * n'échange ici que leur présence et une URL d'aperçu horodatée pour forcer le
 * rechargement après remplacement.
 */
export const AttestationTemplateDialog = memo(function AttestationTemplateDialog({
  channelId, onClose,
}: AttestationTemplateDialogProps) {
  const [template, setTemplate] = useState<AttestationTemplate>(EMPTY_TEMPLATE)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Change à chaque upload/suppression pour casser le cache de l'aperçu <img>.
  const [imageVersion, setImageVersion] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    attestationApi.getTemplate(channelId)
      .then(result => { if (!cancelled) setTemplate(result) })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [channelId])

  const patch = useCallback((field: keyof AttestationTemplate) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setTemplate(prev => ({ ...prev, [field]: event.target.value })), [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const saved = await attestationApi.saveTemplate(channelId, {
        directorName:  template.directorName,
        directorTitle: template.directorTitle,
        city:          template.city,
        introText:     template.introText,
      })
      setTemplate(saved)
      setNotice('Modèle enregistré.')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }, [channelId, template])

  const handleUpload = useCallback(async (kind: AttestationImageKind, file: File) => {
    setError(null)
    setNotice(null)
    try {
      await attestationApi.uploadImage(channelId, kind, file)
      setTemplate(prev => ({ ...prev, [kind === 'logo' ? 'hasLogo' : 'hasSignature']: true }))
      setImageVersion(version => version + 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [channelId])

  const handleDeleteImage = useCallback(async (kind: AttestationImageKind) => {
    setError(null)
    setNotice(null)
    try {
      await attestationApi.deleteImage(channelId, kind)
      setTemplate(prev => ({ ...prev, [kind === 'logo' ? 'hasLogo' : 'hasSignature']: false }))
      setImageVersion(version => version + 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [channelId])

  return (
    <ModalShell title="Modèle d’attestation de diffusion" size="medium" onClose={onClose} closeDisabled={saving}>
      <div className="tp-modal__body attestation-template">
        {loading && <p>Chargement du modèle…</p>}

        {!loading && (
          <>
            <p className="attestation-template__hint">
              Ce modèle s’applique aux attestations générées pour cette chaîne : émissions comme
              spots publicitaires. Le logo apparaît en en-tête, la signature au-dessus du nom.
            </p>

            <div className="attestation-template__grid">
              <label className="reports-field">
                <span className="tp-field-label">Nom du directeur / de la directrice</span>
                <input className="tp-input" value={template.directorName} onChange={patch('directorName')} placeholder="Marie Dupont" />
              </label>
              <label className="reports-field">
                <span className="tp-field-label">Fonction</span>
                <input className="tp-input" value={template.directorTitle} onChange={patch('directorTitle')} placeholder="Directrice d’antenne" />
              </label>
              <label className="reports-field">
                <span className="tp-field-label">Ville (lieu de signature)</span>
                <input className="tp-input" value={template.city} onChange={patch('city')} placeholder="Périgueux" />
              </label>
            </div>

            <label className="reports-field">
              <span className="tp-field-label">Texte d’introduction (facultatif)</span>
              <textarea
                className="tp-input attestation-template__intro"
                value={template.introText}
                onChange={patch('introText')}
                rows={3}
                placeholder="Laisser vide pour un texte généré automatiquement selon le contenu attesté."
              />
            </label>

            <div className="attestation-template__images">
              <ImageField
                label="Logo d’en-tête"
                kind="logo"
                present={template.hasLogo}
                url={template.hasLogo ? `${attestationApi.imageUrl(channelId, 'logo')}&v=${imageVersion}` : null}
                onUpload={handleUpload}
                onDelete={handleDeleteImage}
              />
              <ImageField
                label="Signature"
                kind="signature"
                present={template.hasSignature}
                url={template.hasSignature ? `${attestationApi.imageUrl(channelId, 'signature')}&v=${imageVersion}` : null}
                onUpload={handleUpload}
                onDelete={handleDeleteImage}
              />
            </div>

            {notice && <div className="tp-feedback tp-feedback--success" role="status">{notice}</div>}
          </>
        )}

        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}
      </div>

      <footer className="tp-modal__footer">
        <button type="button" className="tp-button" onClick={onClose} disabled={saving}>Fermer</button>
        <button type="button" className="tp-button tp-button--primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </footer>
    </ModalShell>
  )
})

interface ImageFieldProps {
  label:    string
  kind:     AttestationImageKind
  present:  boolean
  url:      string | null
  onUpload: (kind: AttestationImageKind, file: File) => void
  onDelete: (kind: AttestationImageKind) => void
}

const ImageField = memo(function ImageField({ label, kind, present, url, onUpload, onDelete }: ImageFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) onUpload(kind, file)
    event.target.value = '' // autorise un ré-upload du même fichier
  }, [kind, onUpload])

  const handlePick = useCallback(() => inputRef.current?.click(), [])
  const handleDelete = useCallback(() => onDelete(kind), [kind, onDelete])

  return (
    <div className="attestation-image">
      <span className="tp-field-label">{label}</span>
      <div className="attestation-image__preview" data-empty={present ? undefined : 'true'}>
        {url ? <img src={url} alt={label} /> : <span className="attestation-image__placeholder">Aucune image</span>}
      </div>
      <div className="attestation-image__actions">
        <button type="button" className="tp-button" onClick={handlePick}>
          <ImageUp size={13} aria-hidden="true" />
          {present ? 'Remplacer' : 'Importer'}
        </button>
        {present && (
          <button type="button" className="tp-button tp-button--danger" onClick={handleDelete}>
            <Trash2 size={13} aria-hidden="true" />
            Retirer
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg" hidden onChange={handleChange} />
    </div>
  )
})
